import type { AccessLevel } from '../../../interfaces/Access.interface';
import type { BuildingId } from '../../../interfaces/Building.interface';
import type { UnzonedDateTime } from '../../../interfaces/Date.interface';
import type { SensorTimeseriesId } from '../../../interfaces/Timeseries.interface';
import type { UnitCode } from '../../../interfaces/Unit.interface';
import type {
    AlertOccupancyMode,
    AlertScenarioId,
    AlertScenarioStatus,
    AlertScenarioType,
    AlertTypeCode,
    DayMonth,
    DayRange
} from '../alerts.interface';

export interface ThresholdScenario {
    alertScenarioId: AlertScenarioId;
    scenarioType: AlertScenarioType.THRESHOLD;
    status: AlertScenarioStatus;
    name: string;
    access: AccessLevel;
    measureType: MeasureTypeCode;
    unitCode: UnitCode;
    thresholdLow: number;
    thresholdHigh: number;
    period: {
        occupancy: AlertOccupancyMode;
        alertHolidays: boolean;
        alertPublicHolidays: boolean;
        periodStart: DayMonth;
        periodEnd: DayMonth;
    };
    weeklyRanges: {
        isActivated: boolean;
        ranges: DayRange[];
    }[];
}

export interface ThresholdScenarioSummary {
    alertScenarioId: AlertScenarioId;
    scenarioType: AlertScenarioType;
    name: string;
    measureType: MeasureTypeCode;
    occupancy: AlertOccupancyMode;
    alertCount: number;
    impactedBuildingCount: number;
    buildingCount: number;
    impactedSensorCount: number;
    sensorCount: number;
    status: AlertScenarioStatus;
    access: AccessLevel;
    alertProgress: number | null;
    impactedBuildingProgress: number | null;
    impactedSensorProgress: number | null;
}

export interface ThresholdScenarioGlobalSummary {
    alertCount: number;
    impactedBuildingCount: number;
    buildingCount: number;
    impactedSensorCount: number;
    sensorCount: number;
    alertProgress: number | null;
    impactedBuildingProgress: number | null;
    impactedSensorProgress: number | null;
}

export interface ThresholdScenarioStat {
    buildingId: BuildingId;
    sensorCount: number;
    alertCount: number;
}

export interface ThresholdScenarioSensorStat {
    timeseriesId: SensorTimeseriesId;
    alertCount: number;
}

export enum MeasureTypeCode {
    TEMPERATURE = 'TEMPERATURE',
    HUMIDITY = 'HUMIDITY',
    AIR_QUALITY = 'AIR_QUALITY',
    POWER = 'POWER',
    FLOW_RATE = 'FLOW_RATE',
    PRESSURE = 'PRESSURE',
    LUMINOSITY = 'LUMINOSITY'
}

export enum ThresholdAlertTypeCode {
    INFERIOR_THRESHOLD = 'inferior_threshold',
    SUPERIOR_THRESHOLD = 'superior_threshold'
}

export interface ThresholdAlertScenarioCreation {
    name: string;
    measureType: MeasureTypeCode;
    unit: UnitCode;
}

export interface WaterLeakageScenarioCreation {
    name: string;
}

export interface ThresholdAlertScenarioUpdate {
    name?: string;
    thresholdLow?: number;
    thresholdHigh?: number;
    period?: {
        occupancy?: AlertOccupancyMode;
        alertHolidays?: boolean;
        alertPublicHolidays?: boolean;
        periodStart?: DayMonth;
        periodEnd?: DayMonth;
    };
    weeklyRanges?: {
        isActivated: boolean;
        ranges: DayRange[];
    }[];
}

export interface ThresholdScenarioAlertsTimeseriesAnalysis {
    alertTimeseries: ThresholdAlertTimeseriesPoint[];
    alertScenarioThresholdTimeseries: ThresholdAlertScenarioTimeseriesPoint[];
}

export interface ThresholdAlertTimeseriesPoint {
    datetime: UnzonedDateTime;
    value: number;
    alertTypeCode: AlertTypeCode;
}

export interface ThresholdAlertScenarioTimeseriesPoint {
    lowBound: number;
    highBound: number;
    datetime: UnzonedDateTime;
    activeAfter: boolean;
    inactivityStatuses: string[];
}

export interface ThresholdAlertScenariosFilters {
    search: string | null;
    measureType: MeasureTypeCode | null;
    status: AlertScenarioStatus | null;
}
